import { ShieldOff } from "lucide-react";

type FeatureAccessNoticeProps = {
  title: string;
  body?: string;
};

/**
 * What a case-detail tab (Assessment Prep, Assessment Scribe, Report
 * Builder) renders instead of its content when the viewer's user type has
 * had the tab's capability toggled off (MDRS-443).
 *
 * Mirrors `CaseAccessNotice`'s visual language (icon badge, title, body,
 * `role="status"`/`aria-live`) used for the case-level 403/404 states, but
 * for a capability check rather than a fetch outcome — no retry/back-link:
 * there's nothing to retry, and the tab bar above this notice already
 * offers navigation to a tab the viewer does have access to.
 */
export const FeatureAccessNotice = ({ title, body }: FeatureAccessNoticeProps) => (
  <section
    role="status"
    aria-live="polite"
    className="flex h-full items-center justify-center rounded-xl border border-border bg-background-white p-6"
  >
    <div className="flex max-w-[52ch] flex-col items-center gap-5 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-warning-50 ring-1 ring-warning-100">
        <ShieldOff className="size-6 text-warning-600" aria-hidden="true" />
      </span>

      <div className="space-y-2">
        <h2 className="text-balance text-lg font-semibold text-foreground">
          {title}
        </h2>
        {body && (
          <p className="text-pretty text-sm leading-relaxed text-foreground-muted">
            {body}
          </p>
        )}
      </div>
    </div>
  </section>
);
